import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { buildCreateApi, coreModule, reactHooksModule } from '@reduxjs/toolkit/query/react'

import Parametros from 'Interface/Requisicao';
import Usuario, { FinderUsuarios, RetornoUsuario, Token } from 'Interface/Usuarios';
import { Estado } from 'Store';

import { formData, paramsParaString } from './utils';

const createApi = buildCreateApi(
    coreModule(),
    reactHooksModule({ unstable__sideEffectsInRender: true })
);

export const api = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: `${window.location.origin}/api/v1`,
        prepareHeaders: function (cabecalhos, { getState }) {
            const token = (getState() as Estado).autenticacao.token;
            if (token) cabecalhos.set('authorization', `Bearer ${token}`);

            return cabecalhos;
        },
    }),
    tagTypes: [
        'Perfil',
        'Usuarios',
        'Departamentos',
    ],
    endpoints: construtor => ({
        /**
         * Usuários
         */
        login: construtor.mutation<RetornoUsuario & Token, Partial<Usuario>>({
            query: dados => ({ url: '/usuarios/login', method: 'POST', body: dados }),
        }),
        logout: construtor.mutation<RetornoUsuario & Token, void>({
            query: () => ({ url: '/usuarios/logout', method: 'POST' }),
        }),
        recuperarPerfil: construtor.query<RetornoUsuario, void>({
            query: () => '/usuarios/perfil',
            providesTags: () => [{ type: 'Perfil' }],
        }),
        editarPerfil: construtor.mutation<Usuario, Partial<Usuario> & Pick<Usuario, 'id'>>({
            query: (dados) => ({ url: '/usuarios/perfil', method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Perfil' }] : [],
        }),
        recuperarUsuarios: construtor.query<FinderUsuarios, Parametros | void>({
            query: (params) => `/usuarios?${paramsParaString(params)}`,
            providesTags: (resultado) => resultado?.usuarios
                ? [
                    ...resultado.usuarios.map(({ id }) => ({ type: 'Usuarios', id } as const)),
                    { type: 'Usuarios', id: 'LISTA' },
                ]
                : [{ type: 'Usuarios', id: 'LISTA' }],
        }),
        recuperarUsuario: construtor.query<RetornoUsuario, string | number>({
            query: id => `/usuarios/${id}`,
            providesTags: (resultado, erro, id) => [{ type: 'Usuarios', id }],
        }),
        adicionarUsuario: construtor.mutation<Usuario, Partial<Usuario>>({
            query: (dados) => ({ url: '/usuarios', method: 'POST', body: formData(dados, 'POST') }),
            invalidatesTags: (resultado, erro) => !erro ? [{ type: 'Usuarios', id: 'LISTA' }] : [],
        }),
        editarUsuario: construtor.mutation<Usuario, Partial<Usuario> & Pick<Usuario, 'id'>>({
            query: ({ id, ...dados }) => ({ url: `/usuarios/${id}`, method: 'POST', body: formData(dados, 'PUT') }),
            invalidatesTags: (resultado, erro, { id }) => !erro ? [{ type: 'Usuarios', id }] : [],
        }),
    }),
})

export default api;

export const {
    useRecuperarUsuariosQuery,
    useRecuperarUsuarioQuery,
    useAdicionarUsuarioMutation,
    useEditarUsuarioMutation,
    useLoginMutation,
    useLogoutMutation,
    useRecuperarPerfilQuery,
    useEditarPerfilMutation,
} = api;
